// Third-party
import React from 'react';
import ReactDOMServer from 'react-dom/server';

// Namespace
import * as ProposalNS from '../Proposal';

// Constants and types
import { PROPOSAL_ORIENTATION } from '../utils/constants';
import ComparisonItem from '@/types/comparison-item';
import CorrelationMatrixData from '@/types/correlation-matrix-data';
import { ComparisonToolbarKeys, TIME_RANGE_PRESET } from '../../../utils/constants';

// Utils
import { LOCALE, formatLocalizedString } from '../../../utils/i18n';
import { CORRELATION_MATRIX_LAYOUT } from '../utils/correlation-matrix';

// Components
import HoldingIcon, { Props as HoldingIconProps } from '../../../views/components/common/HoldingIcon';

const legendStr = `
  <div class="correlation-legend">
    <div class="correlation-scale"></div>
    <div class="correlation-legend-labels">
      <span class="upperbound">1</span>
      <span class="zero-value">0</span>
      <span class="lowerbound">-1</span>
    </div>
  </div>
`;

const legendElement: ProposalNS.ProposalTemplateElement = {
  type: ProposalNS.ProposalTemplateElementType.Data,
  dataView: {
    type: ProposalNS.DataView.Html,
    className: 'no-border',
  },
  DAO: {
    type: ProposalNS.DAOType.HTMLContent,
    htmlContentDAOOptions: {
      html: [
        {
          content: {
            'en-CA': legendStr,
            'fr-CA': legendStr,
          },
        },
      ],
    },
  },
};

const buildMatrix = (
  index: number,
  item: ComparisonItem,
  templateOptions: ProposalNS.ProposalTemplateOptions,
  includeLegend: boolean,
  itemTypes?: [ProposalNS.ProposalItemType],
  extraDisclaimers?: ProposalNS.CustomDisclaimerConfig[],
): ProposalNS.ProposalTemplateLayoutElement => {
  const { locale } = templateOptions;

  const holdingIcon: string = ReactDOMServer.renderToString(
    React.createElement<HoldingIconProps>(HoldingIcon, {
      type: item.type,
      color: item.color,
      size: item.type === 'benchmark' ? 10 : 15,
      boxPad: 5,
    }),
  );

  const formattedItemName = `
    <div class="correlation-item-name markup-container">
    ${holdingIcon}
      <span class="item-label">${formatLocalizedString(item.name, locale)}</span>
    </div>
  `;

  let correlationElements: ProposalNS.ProposalTemplateElement[] = [
    {
      type: ProposalNS.ProposalTemplateElementType.Data,
      dataView: {
        type: ProposalNS.DataView.Table,
        showHeader: false,
      },
      DAO: {
        type: ProposalNS.DAOType.CorrelationMatrix,
        filterItemsByIndexDAOOptions: {
          selection: [index],
          itemTypes,
        },
        ...(extraDisclaimers && extraDisclaimers.length > 0 ? { customDisclaimerDAOOptions: { configs: extraDisclaimers } } : {}),
      },
      toolbarOptionConfig: {
        key: 'CORRELATION_MATRICES',
      },
    },
  ];

  if (includeLegend) {
    correlationElements = [legendElement, ...correlationElements];
  }

  return {
    type: ProposalNS.ProposalTemplateElementType.Layout,
    layout: {
      type: ProposalNS.LayoutType.Grid,
      columns: 1,
      className: 'correlation-matrix-container',
    },
    elements: [
      {
        type: ProposalNS.ProposalTemplateElementType.Data,
        dataView: {
          type: ProposalNS.DataView.Html,
          className: 'no-border',
        },
        DAO: {
          type: ProposalNS.DAOType.HTMLContent,
          htmlContentDAOOptions: {
            html: [
              {
                content: {
                  'en-CA': formattedItemName,
                  'fr-CA': formattedItemName,
                },
              },
            ],
          },
        },
      },
      {
        type: ProposalNS.ProposalTemplateElementType.Layout,
        layout: {
          type: ProposalNS.LayoutType.Grid,
          columns: 1,
          className: 'correlation-matrix-item',
        },
        elements: correlationElements,
      },
    ],
  };
};

const buildSingleMatrix = (
  templateOptions: ProposalNS.ProposalTemplateOptions,
  items: ComparisonItem[],
  indices: number[],
  itemTypes?: [ProposalNS.ProposalItemType],
  extraDisclaimers?: ProposalNS.CustomDisclaimerConfig[],
): ProposalNS.ProposalTemplateLayoutElement => {
  const outElement = {
    type: ProposalNS.ProposalTemplateElementType.Layout,
    layout: {
      type: ProposalNS.LayoutType.Grid,
      columns: 1,
    },
    elements: indices.map(index => buildMatrix(index, items[index], templateOptions, true, itemTypes, extraDisclaimers)),
  };

  return outElement as ProposalNS.ProposalTemplateLayoutElement;
};

const buildMultipleMatrices = (
  templateOptions: ProposalNS.ProposalTemplateOptions,
  items: ComparisonItem[],
  indices: number[],
  inline: boolean,
  itemTypes?: [ProposalNS.ProposalItemType],
  extraDisclaimers?: ProposalNS.CustomDisclaimerConfig[],
): ProposalNS.ProposalTemplateLayoutElement => {
  let matrices: ProposalNS.ProposalTemplateLayoutElement[];

  if (!inline) {
    // If not inline, we only render the items for which we have data (ie: only portfolios)
    matrices = indices.map(index => buildMatrix(index, items[index], templateOptions, !inline, itemTypes, extraDisclaimers));
  } else {
    // Else we render all items in the proposal, to preserve visual alignment with other sections
    matrices = items.map((item, index) => buildMatrix(index, item, templateOptions, !inline, itemTypes, extraDisclaimers));
  }

  let columns: number | undefined;
  let elements: ProposalNS.ProposalTemplateElement[] = matrices;

  if (!inline) {
    // Pages dedicated to correlation matrices use a specific column layout.
    columns = templateOptions.styleOptions.orientation === PROPOSAL_ORIENTATION.PORTRAIT ? 2 : 3;
  } else {
    // Inline shows the legend element only once.
    elements = [legendElement, ...matrices];
    columns = elements.length;
  }

  const outElement = {
    type: ProposalNS.ProposalTemplateElementType.Layout,
    layout: {
      type: ProposalNS.LayoutType.Grid,
      columns,
    },
    elements,
  };

  return outElement as ProposalNS.ProposalTemplateLayoutElement;
};

const buildMultipleLargeMatrices = (
  templateOptions: ProposalNS.ProposalTemplateOptions,
  items: ComparisonItem[],
  indices: number[],
  itemTypes?: [ProposalNS.ProposalItemType],
  extraDisclaimers?: ProposalNS.CustomDisclaimerConfig[],
): ProposalNS.ProposalTemplateLayoutElement => {
  const outElement = {
    type: ProposalNS.ProposalTemplateElementType.Layout,
    layout: {
      type: ProposalNS.LayoutType.Grid,
      columns: 2,
    },
    elements: indices.map(index => buildMatrix(index, items[index], templateOptions, true, itemTypes, extraDisclaimers)),
  };

  return outElement as ProposalNS.ProposalTemplateLayoutElement;
};

const setParsedDescription = (text?: string) => {
  if (!text) {
    return '';
  }

  if (text.indexOf('{{period}}') >= 0) {
    text = text.replaceAll('{{period}}', '{{proposal.toolbarOptions.CORRELATION_MATRICES.periodLabel.locale}}');
  }

  return text;
};

export const buildCorrelationMatricesSections = (
  section: ProposalNS.ProposalTemplateSectionElement,
  data: CorrelationMatrixData[],
  items: ComparisonItem[],
  templateOptions: ProposalNS.ProposalTemplateOptions,
  inline: boolean,
  itemTypes?: [ProposalNS.ProposalItemType],
): ProposalNS.ProposalTemplateSectionElement[] => {
  const newSections: ProposalNS.ProposalTemplateSectionElement[] = [];

  const extraDisclaimers = section.extraDisclaimers ?? [];

  for (const datum of data) {
    const matrices = [];

    switch (datum.type) {
      case CORRELATION_MATRIX_LAYOUT.SINGLE:
        matrices.push(
          buildSingleMatrix(
            templateOptions,
            items,
            datum.items.map(item => items.findIndex(i => i._id === item._id)),
            itemTypes,
            extraDisclaimers,
          ),
        );
        break;
      case CORRELATION_MATRIX_LAYOUT.MULTIPLE:
        matrices.push(
          buildMultipleMatrices(
            templateOptions,
            items,
            datum.items.map(item => items.findIndex(i => i._id === item._id)),
            inline,
            itemTypes,
            extraDisclaimers,
          ),
        );
        break;
      case CORRELATION_MATRIX_LAYOUT.MULTIPLE_LARGE:
        matrices.push(
          buildMultipleLargeMatrices(
            templateOptions,
            items,
            datum.items.map(item => items.findIndex(i => i._id === item._id)),
            itemTypes,
            extraDisclaimers,
          ),
        );
        break;
    }

    const newSection = {
      type: section.type,
      key: `${section.key}`,
      label: section.label,
      forced: true,
      pageFraction: {
        Portrait: 1,
        Landscape: 1,
      },
      showTitle: false,
      sidebarControl: ProposalNS.ProposalSidebarControl.Hide,
      buildRules: section.buildRules,
      layout: {
        type: ProposalNS.ProposalTemplateElementType.Layout,
        layout: {
          type: ProposalNS.LayoutType.Grid,
          columns: 1,
        },
        elements: [
          {
            type: 'LAYOUT',
            layout: {
              type: 'GRID',
              columns: 2,
              className: 'title-grid',
            },
            elements: [
              {
                type: 'DATA',
                dataView: {
                  type: 'TEXT',
                  className: 'proposal-section-header no-border',
                },
                DAO: {
                  type: 'TEXT',
                  localizedTextDAOOptions: {
                    texts: [
                      {
                        tag: 'h2',
                        text: {
                          'en-CA': 'Correlation Matrices',
                          'fr-CA': 'Matrices de corrélation',
                        },
                      },
                      {
                        text: {
                          'en-CA': '({{proposal.toolbarOptions.CORRELATION_MATRICES.periodLabel.locale}})',
                          'fr-CA': '({{proposal.toolbarOptions.CORRELATION_MATRICES.periodLabel.locale}})',
                        },
                        className: 'selected-period print-only',
                      },
                    ],
                  },
                },
              },
              {
                type: 'DATA',
                dataView: {
                  type: 'CHART_TOOLBAR',
                },
                DAO: {
                  type: 'TOOLBAR',
                },
                toolbarOptionConfig: {
                  key: 'CORRELATION_MATRICES',
                  comparisonToolbarKey: ComparisonToolbarKeys.CORRELATION_MATRICES,
                  toolbarElements: ['DATE_QUICK_RANGE'],
                  dateQuickRangePreset: TIME_RANGE_PRESET.PORTFOLIO_CORRELATION_MATRIX,
                },
              },
              {
                type: 'DATA',
                dataView: {
                  type: 'TEXT',
                  className: 'visually-hidden',
                },
                DAO: {
                  type: 'TEXT',
                  localizedTextDAOOptions: {
                    texts: [
                      {
                        tag: 'div',
                        text: {
                          'en-CA': section.accessibilityDescription
                            ? setParsedDescription(section.accessibilityDescription?.[templateOptions.locale || LOCALE.EN_CA])
                            : '',
                          'fr-CA': section.accessibilityDescription
                            ? setParsedDescription(section.accessibilityDescription?.[templateOptions.locale || LOCALE.FR_CA])
                            : '',
                        },
                      },
                    ],
                  },
                },
              },
            ],
          },
          ...matrices,
        ],
      },
    } as ProposalNS.ProposalTemplateSectionElement;

    newSections.push(newSection);
  }

  return newSections;
};
